// Supabase External Provider Invitation Service - Production Ready
// Handles external provider invitations in Supabase database

import { supabase } from './supabase';
import { logger } from '../utils/logger';

export const externalInvitationService = {
  /**
   * Create a new external provider invitation
   * @param {Object} invitationData - Invitation details
   * @returns {Promise<Object|null>} Created invitation or null
   */
  async createInvitation(invitationData) {
    try {
      logger.debug('➕ Creating external provider invitation in Supabase:', invitationData);

      const { data: invitation, error } = await supabase
        .from('external_provider_invitations')
        .insert([{
          token: invitationData.token,
          provider_npi: invitationData.providerNPI,
          provider_name: invitationData.providerName,
          provider_email: invitationData.providerEmail,
          provider_specialty: invitationData.providerSpecialty || null,
          provider_hospital: invitationData.providerHospital || null,
          provider_location: invitationData.providerLocation || null,
          sales_rep_id: invitationData.salesRepId,
          sales_rep_name: invitationData.salesRepName,
          sales_rep_email: invitationData.salesRepEmail,
          sales_rep_company: invitationData.salesRepCompany || null,
          sales_rep_phone: invitationData.salesRepPhone || null,
          meeting_date: invitationData.meetingDate,
          meeting_time: invitationData.meetingTime,
          restaurant_name: invitationData.restaurant || null,
          restaurant_address: invitationData.restaurantAddress || null,
          restaurant_yelp_id: invitationData.restaurantYelpId || null,
          notes: invitationData.notes || null,
          status: 'pending',
          email_sent: invitationData.emailSent || false,
          email_message_id: invitationData.emailMessageId || null,
          email_sent_at: invitationData.emailSent ? new Date().toISOString() : null
        }])
        .select()
        .single();

      if (error) {
        logger.error('Error creating external invitation in Supabase', { error: error.message });
        throw error;
      }

      logger.debug('✅ External invitation created successfully:', invitation);
      return invitation;
    } catch (error) {
      logger.error('Error creating external invitation', { error: error.message });
      return null;
    }
  },

  /**
   * Get all invitations for a specific sales rep
   * @param {string} salesRepId - Sales rep UUID
   * @returns {Promise<Array>} Array of invitations
   */
  async getInvitationsBySalesRep(salesRepId) {
    try {
      const { data: invitations, error } = await supabase
        .from('external_provider_invitations')
        .select('*')
        .eq('sales_rep_id', salesRepId)
        .order('created_at', { ascending: false });

      if (error) {
        logger.error('Error loading invitations from Supabase', { error: error.message });
        throw error;
      }

      return invitations || [];
    } catch (error) {
      logger.error('Error getting invitations', { error: error.message });
      return [];
    }
  },

  /**
   * Get invitation by token (for provider response)
   * @param {string} token - Invitation token
   * @returns {Promise<Object|null>} Invitation object or null
   */
  async getInvitationByToken(token) {
    try {
      const { data: invitation, error } = await supabase
        .from('external_provider_invitations')
        .select('*')
        .eq('token', token)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No rows returned
          logger.debug('Invitation not found for token');
          return null;
        }
        logger.error('Error loading invitation by token', { error: error.message });
        throw error;
      }

      return invitation;
    } catch (error) {
      logger.error('Error getting invitation by token', { error: error.message });
      return null;
    }
  },

  /**
   * Update invitation status (for provider response)
   * @param {string} token - Invitation token
   * @param {string} status - New status (confirmed, declined, expired)
   * @param {Object} additionalData - Additional data to update
   * @returns {Promise<Object|null>} Updated invitation or null
   */
  async updateInvitationStatus(token, status, additionalData = {}) {
    try {
      logger.debug('Updating invitation status:', { token, status, additionalData });

      const updateData = {
        status,
        responded_at: new Date().toISOString(),
        response_method: additionalData.responseMethod || 'email_link',
        ...additionalData
      };

      const { data: invitation, error } = await supabase
        .from('external_provider_invitations')
        .update(updateData)
        .eq('token', token)
        .select()
        .single();

      if (error) {
        logger.error('Error updating invitation status', { error: error.message });
        throw error;
      }

      logger.debug('✅ Invitation status updated successfully:', invitation);
      return invitation;
    } catch (error) {
      logger.error('Error updating invitation status', { error: error.message });
      return null;
    }
  },

  /**
   * Mark invitation as having created a meeting
   * @param {string} invitationId - Invitation UUID
   * @param {string} meetingId - Created meeting UUID
   * @returns {Promise<Object|null>} Updated invitation or null
   */
  async markMeetingCreated(invitationId, meetingId) {
    try {
      const { data: invitation, error } = await supabase
        .from('external_provider_invitations')
        .update({
          meeting_created: true,
          meeting_id: meetingId,
          status: 'meeting_created'
        })
        .eq('id', invitationId)
        .select()
        .single();

      if (error) {
        logger.error('Error marking meeting as created', { error: error.message });
        throw error;
      }

      return invitation;
    } catch (error) {
      logger.error('Error marking meeting created', { error: error.message });
      return null;
    }
  },

  /**
   * Get pending invitations (not expired)
   * @param {string} salesRepId - Optional sales rep UUID to filter
   * @returns {Promise<Array>} Array of pending invitations
   */
  async getPendingInvitations(salesRepId = null) {
    try {
      let query = supabase
        .from('external_provider_invitations')
        .select('*')
        .eq('status', 'pending')
        .gt('expires_at', new Date().toISOString());

      if (salesRepId) {
        query = query.eq('sales_rep_id', salesRepId);
      }

      const { data: invitations, error } = await query.order('created_at', { ascending: false });

      if (error) {
        logger.error('Error loading pending invitations', { error: error.message });
        throw error;
      }

      return invitations || [];
    } catch (error) {
      logger.error('Error getting pending invitations', { error: error.message });
      return [];
    }
  },

  /**
   * Mark expired invitations (called periodically or on-demand)
   * @returns {Promise<number>} Number of invitations marked as expired
   */
  async markExpiredInvitations() {
    try {
      const { data: expiredInvitations, error } = await supabase
        .from('external_provider_invitations')
        .update({ status: 'expired' })
        .eq('status', 'pending')
        .lt('expires_at', new Date().toISOString())
        .select();

      if (error) {
        logger.error('Error marking expired invitations', { error: error.message });
        throw error;
      }

      const count = expiredInvitations?.length || 0;
      if (count > 0) {
        logger.debug(`✅ Marked ${count} invitations as expired`);
      }
      return count;
    } catch (error) {
      logger.error('Error marking expired invitations', { error: error.message });
      return 0;
    }
  },

  /**
   * Check if invitation is expired
   * @param {Object} invitation - Invitation object
   * @returns {boolean} Whether invitation is expired
   */
  isInvitationExpired(invitation) {
    if (!invitation || !invitation.expires_at) return true;
    return new Date() > new Date(invitation.expires_at);
  },

  /**
   * Get invitation statistics for a sales rep
   * @param {string} salesRepId - Sales rep UUID
   * @returns {Promise<Object>} Statistics object
   */
  async getInvitationStats(salesRepId) {
    try {
      const { data: invitations, error } = await supabase
        .from('external_provider_invitations')
        .select('status')
        .eq('sales_rep_id', salesRepId);

      if (error) throw error;

      const stats = {
        total: invitations?.length || 0,
        pending: invitations?.filter(i => i.status === 'pending').length || 0,
        confirmed: invitations?.filter(i => i.status === 'confirmed').length || 0,
        declined: invitations?.filter(i => i.status === 'declined').length || 0,
        expired: invitations?.filter(i => i.status === 'expired').length || 0,
        meetingCreated: invitations?.filter(i => i.status === 'meeting_created').length || 0
      };

      return stats;
    } catch (error) {
      logger.error('Error getting invitation stats', { error: error.message });
      return { total: 0, pending: 0, confirmed: 0, declined: 0, expired: 0, meetingCreated: 0 };
    }
  }
};

export default externalInvitationService;
